// @avantia common-code
import { ensureDefined, getFromWindowRef } from '@avantia/client-and-server-utilities';
import jQuery from 'jquery';
import {
  HpClientQuestionSetState,
  HpClientSection,
  HpClientSectionMap,
  HpClientValidationResult,
  HpGlobalEngineState,
  HpQuestionDisplayState,
  HpUserInterfaceState,
  HpViewportSizeTypes,
  HpViewportState
} from '../types';
import { HpFeatureToggleState } from '../types/featureToggle';
import { applyChanges } from './reducerLibrary';

export function createEmptyValidationResult(): HpClientValidationResult {
  return {
    count: 0,
    errors: {},
    checked: {},
    validSections: []
  };
}

export function createEmptyFeatureToggleState(): HpFeatureToggleState {
  return {};
}

export function createEmptyClientQuestionSetState(): HpClientQuestionSetState {
  return {
    productId: '',
    productDescription: '',
    questionSetId: '',
    answersId: '',
    friendlyName: '',
    timestamp: '',
    validFrom: '',
    validTo: '',
    version: '',
    hashCode: null,
    stylesheets: [],
    questionFormats: [],
    questions: {},
    answers: {},
    displayState: createEmptyDisplayState({ addBlankSection: false }),
    validationResult: createEmptyValidationResult(),
    commonLookups: {},
    commonHelpText: {},
    commonInfoText: {},
    commonWarningText: {}
  };
}

export function createEmptyUserInterfaceState(): HpUserInterfaceState {
  const interfaceState: HpUserInterfaceState = {
    fetchError: undefined,
    ajaxCallsInProgress: 0,
    disableButtons: false,
    ajaxCallsMap: {},
    viewport: createViewportState()
  };
  return interfaceState;
}

const falseFunc: () => false = () => false;

export interface HpCreateEmptyDisplayStateOptions {
  addBlankSection: boolean;
  extra?: Partial<HpQuestionDisplayState>;
}

export function createEmptyDisplayState({
  addBlankSection,
  extra
}: HpCreateEmptyDisplayStateOptions): HpQuestionDisplayState {
  let sectionId = '';
  let sections: HpClientSectionMap = {};
  let sectionIdOrder: string[] = [];
  if (addBlankSection) {
    sectionId = 'none';
    const none: HpClientSection = {
      sectionId,
      title: 'None',
      position: 0,
      isRepeater: false,
      prerequisites: [falseFunc]
    };
    sections = { none };
    sectionIdOrder = [sectionId];
  }

  const state: HpQuestionDisplayState = {
    sections,
    sectionIdOrder,
    activeSectionId: sectionId,
    highestSectionStartedPosition: 0,
    visibleQuestionIds: [],
    allOrderedQuestionIds: [],
    availableQuestionSets: { items: [], selected: undefined },
    questionIdWithFocus: undefined,
    sectionsUI: addBlankSection ? { none: { isVisible: false } } : {},
    questionsUI: {},
    scriptActions: [],
    showValidationSummary: false
  };

  return applyChanges(state, extra || {});
}

export function createViewportState(): HpViewportState {
  interface ViewportWindowProps {
    document: Document;
  }

  const { document } = getFromWindowRef<ViewportWindowProps>(['document']) || {};
  if (document) {
    const win = jQuery(window);
    const width = win.width() as number;
    const height = win.height() as number;

    let isTablet = false,
      isMobile = false,
      isDesktop = false;
    let size: HpViewportSizeTypes;
    if (width <= 700) {
      isMobile = true;
      size = 'mobile';
    } else if (width <= 1032) {
      isTablet = true;
      size = 'tablet';
    } else {
      isDesktop = true;
      size = 'desktop';
    }

    return { width, height, isDesktop, isTablet, isMobile, size };
  }

  return { width: 0, height: 0, isMobile: false, isTablet: false, isDesktop: true, size: 'desktop' };
}

export type HpGetIntialStateFunction = () => HpGlobalEngineState;

let getInitStateFunction: HpGetIntialStateFunction | undefined;

export function registerGetInitialState(getInitialState: HpGetIntialStateFunction): void {
  getInitStateFunction = ensureDefined(getInitialState, 'getInitialState');
}

// TODO Minor Provide parameter and don't create all just for one property.
export function getInitialState(): HpGlobalEngineState {
  if (!getInitStateFunction) {
    throw new Error(
      `The 'registerGetInitialEngineState' function must be called prior to a call to the 'getInitialEngineState' function.`
    );
  }

  return getInitStateFunction();
}
